import {_} from 'lodash';
import {IObjectData, IOrganization, IOverride, ISystemPermission} from '../../../index';
import {ObjectTypeId} from '../../../constants';
import {global, lm} from '../../../appContext';
import PromiseRequest from '../../../req/PromiseRequest';
import {OrganizationPanel} from './OrganizationPanel';
import {PermissionRow} from './PermissionRow';
import {nsUserPermissions} from './nsUserPermissions';
import {nsOverride} from '../../override/nsOverride';
import requestRestoreOverride = nsOverride.requestRestoreOverride;

/** Translated reject reason when role/system is missing for a mutation. */
function rejectInvalidPermission(): Promise<never> {
    return Promise.reject(lm.t('user.permissions.deny.invalid') ?? 'user.permissions.deny.invalid');
}

/** Success toast — CMS: `Permission saved|Permissions saved` (and removed/restored). */
export function permissionSnackbar(kind: 'saved' | 'removed' | 'restored', n = 1): void {
    global.snackbarData({message: `user.permissions.snackbar.${kind}|n:${n}`});
}

/** `createoverride` returns `data: IOverride[]` (often length 1). */
function createdOverrideFromResponse(data: IOverride | IOverride[] | null | undefined): IOverride {
    const created = Array.isArray(data) ? data[0] : data;
    if (!created?.id) {
        throw new Error('createoverride returned no override id');
    }
    return created;
}

/** Manual SP change for a single shared updatepermission (bulk edit/delete). */
export type ManualSpOp =
    | {kind: 'replace'; organizationId: string; panel: OrganizationPanel; previousId: number; updated: ISystemPermission}
    | {kind: 'delete'; organizationId: string; panel: OrganizationPanel; deletedId: number}
    | {kind: 'append'; organizationId: string; sp: ISystemPermission};

/**
 * API mutations for permissions-v2 (updatepermission / createoverride / deleteoverride / restoreoverride).
 * Page owns busy UI; this module owns payloads, snackbars, and local SP patches where ids are known.
 */
export class PermissionMutations {
    private readonly reqUpdatePermission = new PromiseRequest('updatepermission');
    private readonly reqCreateOverride = new PromiseRequest('createoverride');
    private readonly reqDeleteOverride = new PromiseRequest('deleteoverride');
    private readonly reqRestoreOverride = new PromiseRequest('restoreoverride');

    /** Build the replace payload for an edited manual row, or null when role/system is missing. */
    buildManualUpdateFromRow(row: PermissionRow): ISystemPermission | null {
        const role = row.role();
        const system = row.system ?? row.systemPermission.system;
        if (!role || !system) {
            return null;
        }
        const isLegalUnit = nsUserPermissions.isLegalUnitScope(row.systemPermission);
        return nsUserPermissions.buildManualSystemPermissionPayload({
            companyid: row.systemPermission.companyid,
            role,
            system,
            departmentId: isLegalUnit
                ? (row.systemPermission.managerOf ?? 0)
                : (row.department()?.id ?? 0),
            departmentName: isLegalUnit
                ? (row.systemPermission.managerOfName ?? '')
                : (row.department()?.name ?? ''),
            expirydateInput: row.expirydate(),
            // Note edits save separately; structural save keeps the stored comment.
            comment: row.systemPermission.comment ?? '',
            id: row.systemPermission.id,
            managerOfObjectTypeId: isLegalUnit
                ? ObjectTypeId.LegalUnit
                : ObjectTypeId.Department,
        });
    }

    /** Manual save: replace one stored SP in updatepermission; patches user + panel locally. */
    saveManualPermission(
        user: IObjectData,
        row: PermissionRow,
        panel: OrganizationPanel,
        visibleOrganizationIds: ReadonlySet<string>,
    ): Promise<void> {
        const updated = this.buildManualUpdateFromRow(row);
        if (!updated) {
            return rejectInvalidPermission();
        }
        return this.saveManualSpOps(
            user,
            visibleOrganizationIds,
            [{
                kind: 'replace',
                organizationId: panel.organization.organizationid,
                panel,
                previousId: row.systemPermission.id,
                updated,
            }],
        ).then(() => permissionSnackbar('saved'));
    }

    /** Inline note-card save: stored role/dept/expiry unchanged; only `comment` updates. */
    savePermissionComment(
        user: IObjectData,
        row: PermissionRow,
        panel: OrganizationPanel,
        visibleOrganizationIds: ReadonlySet<string>,
    ): Promise<void> {
        const sp = row.systemPermission;
        const role = sp.role;
        const system = row.system ?? sp.system;
        if (!role || !system) {
            return rejectInvalidPermission();
        }
        const isLegalUnit = nsUserPermissions.isLegalUnitScope(sp);
        const updated = nsUserPermissions.buildManualSystemPermissionPayload({
            companyid: sp.companyid,
            role,
            system,
            departmentId: sp.managerOf ?? 0,
            departmentName: sp.managerOfName ?? '',
            expirydateInput: nsUserPermissions.convertExpiryDateFromServerString(sp.expirydate) ?? '',
            comment: row.commentDraft(),
            id: sp.id,
            managerOfObjectTypeId: isLegalUnit
                ? ObjectTypeId.LegalUnit
                : ObjectTypeId.Department,
        });
        return this.saveManualSpOps(
            user,
            visibleOrganizationIds,
            [{
                kind: 'replace',
                organizationId: panel.organization.organizationid,
                panel,
                previousId: sp.id,
                updated,
            }],
        ).then(() => permissionSnackbar('saved'));
    }

    /**
     * AP→MP step 1: soft-remove the AP via createoverride (and drop a prior override if any).
     * Returns the new manual SP payload (id 0) for a later updatepermission.
     */
    prepareAutomaticAsManual(user: IObjectData, row: PermissionRow): Promise<ISystemPermission> {
        const newManualBase = this.buildManualUpdateFromRow(row);
        if (!newManualBase) {
            return rejectInvalidPermission();
        }
        const newManual = _.assign({}, newManualBase, {id: 0});
        const removeOverride = nsUserPermissions.buildRemoveOverrideForPermission(row.systemPermission, user);
        if (newManual.expirydate) {
            removeOverride.expires = newManual.expirydate;
        }

        const existingOverride = row.getOwnOverride();
        const deleteOld = existingOverride?.id
            ? this.reqDeleteOverride.request({id: existingOverride.id, orgnr: existingOverride.companyid})
            : Promise.resolve();

        return deleteOld
            .then(() => this.reqCreateOverride.request({
                joverride: JSON.stringify(removeOverride),
                orgnr: removeOverride.companyid,
            }))
            .then(() => newManual);
    }

    /**
     * Automatic edit: R-override the AP, then updatepermission adding a new manual row.
     * Caller must refresh — new MP id is not returned by updatepermission.
     */
    saveAutomaticAsManual(
        user: IObjectData,
        row: PermissionRow,
        panel: OrganizationPanel,
        visibleOrganizationIds: ReadonlySet<string>,
    ): Promise<void> {
        return this.prepareAutomaticAsManual(user, row)
            .then((newManual) => this.saveManualSpOps(
                user,
                visibleOrganizationIds,
                [{kind: 'append', organizationId: panel.organization.organizationid, sp: newManual}],
            ))
            .then(() => permissionSnackbar('saved'));
    }

    /** Manual delete: updatepermission without this permission id; patches user + panel locally. */
    deleteManualPermission(
        user: IObjectData,
        row: PermissionRow,
        panel: OrganizationPanel,
        visibleOrganizationIds: ReadonlySet<string>,
    ): Promise<void> {
        return this.saveManualSpOps(
            user,
            visibleOrganizationIds,
            [{
                kind: 'delete',
                organizationId: panel.organization.organizationid,
                panel,
                deletedId: row.systemPermission.id,
            }],
        ).then(() => permissionSnackbar('removed'));
    }

    /**
     * Modified-pair undo: delete the MP, then delete the paired AP remove-override.
     * One `restored` snackbar; caller should refresh (override list + AP visibility).
     * Pass `pairedOverride` when the draft just became equal (row may not have pairedRemove yet).
     */
    restoreModifiedPair(
        user: IObjectData,
        row: PermissionRow,
        panel: OrganizationPanel,
        visibleOrganizationIds: ReadonlySet<string>,
        pairedOverride?: IOverride | null,
        opts?: {snackbar?: boolean},
    ): Promise<void> {
        const override = pairedOverride ?? row.getPairedRemoveOverride();
        if (!override?.id) {
            return Promise.reject('missing paired override');
        }
        const showSnackbar = opts?.snackbar !== false;
        return this.saveManualSpOps(
            user,
            visibleOrganizationIds,
            [{
                kind: 'delete',
                organizationId: panel.organization.organizationid,
                panel,
                deletedId: row.systemPermission.id,
            }],
        )
            .then(() => this.reqDeleteOverride.request({
                id: override.id,
                orgnr: override.companyid,
            }))
            .then(() => {
                nsUserPermissions.invalidatePermissionCaches();
                if (showSnackbar) {
                    permissionSnackbar('restored');
                }
            });
    }

    /**
     * One updatepermission applying many manual SP replace/delete/append ops across orgs.
     * Append to a tenant missing from user.permission uses a login-org shell (override-only grant).
     * Patches user + panels for replace/delete; appends need a caller refresh (no new ids).
     * No snackbar — caller toasts once (single or bulk).
     */
    saveManualSpOps(
        user: IObjectData,
        visibleOrganizationIds: ReadonlySet<string>,
        ops: ManualSpOp[],
    ): Promise<void> {
        if (!ops.length) {
            return Promise.resolve();
        }

        const organizations = (user.permission ?? [])
            .filter(o => visibleOrganizationIds.has(o.organizationid))
            .map(o => _.cloneDeep(o));

        for (const op of ops) {
            let org = organizations.find(o => o.organizationid == op.organizationId);
            if (!org && op.kind === 'append') {
                // Panel can show an override-only tenant that is not on user.permission yet.
                const shellSource = global.login.getOrganizationById(op.organizationId);
                if (!shellSource) {
                    return Promise.reject('missing organization');
                }
                org = _.cloneDeep(shellSource);
                org.systempermission = [];
                organizations.push(org);
            }
            if (!org) {
                continue;
            }
            const sps: ISystemPermission[] = org.systempermission ?? [];
            if (op.kind === 'replace') {
                org.systempermission = sps.map(sp => sp.id == op.previousId ? op.updated : sp);
            } else if (op.kind === 'delete') {
                org.systempermission = sps.filter(sp => sp.id != op.deletedId);
            } else {
                org.systempermission = [...sps, op.sp];
            }
        }

        return this.reqUpdatePermission
            .request({jobject: JSON.stringify(nsUserPermissions.buildUpdatePermissionJobject(user, organizations))})
            .then(() => {
                for (const op of ops) {
                    if (op.kind === 'replace') {
                        patchOrgSystemPermissions(
                            user,
                            op.organizationId,
                            sps => sps.map(sp => sp.id == op.previousId ? op.updated : sp),
                        );
                        op.panel.replaceSystemPermission(op.updated);
                    } else if (op.kind === 'delete') {
                        patchOrgSystemPermissions(
                            user,
                            op.organizationId,
                            sps => sps.filter(sp => sp.id != op.deletedId),
                        );
                        op.panel.removeSystemPermissionById(op.deletedId);
                    }
                }
                nsUserPermissions.invalidatePermissionCaches();
            });
    }

    /**
     * Automatic delete: createoverride with remove value.
     * Returns the created override (includes server id) for local UI patching.
     * No snackbar — caller toasts once (single or bulk).
     */
    deleteAutomaticPermission(
        user: IObjectData,
        row: PermissionRow,
    ): Promise<IOverride> {
        const removeOverride = nsUserPermissions.buildRemoveOverrideForPermission(row.systemPermission, user);
        const existingOverride = row.getOwnOverride();
        const deleteOld = existingOverride?.id
            ? this.reqDeleteOverride.request({id: existingOverride.id, orgnr: existingOverride.companyid})
            : Promise.resolve();

        return deleteOld
            .then(() => this.reqCreateOverride.request({
                joverride: JSON.stringify(removeOverride),
                orgnr: removeOverride.companyid,
            }) as Promise<IOverride | IOverride[]>)
            .then((data) => {
                const created = createdOverrideFromResponse(data);
                nsUserPermissions.invalidatePermissionCaches();
                return created;
            });
    }

    /** Undo a remove override via `deleteoverride` (hard- or soft-delete on the server). */
    deletePermissionOverride(override: IOverride): Promise<void> {
        if (!override.id) {
            return Promise.reject('missing override');
        }
        return this.reqDeleteOverride
            .request({id: override.id, orgnr: override.companyid})
            .then(() => {
                nsUserPermissions.invalidatePermissionCaches();
                permissionSnackbar('restored');
            });
    }

    /** Sequential `deleteoverride` with no snackbar (bulk auto-restore after MP deletes). */
    deletePermissionOverridesSilent(overrides: IOverride[]): Promise<void> {
        return overrides.reduce<Promise<void>>((chain, override) => {
            if (!override.id) {
                return chain;
            }
            return chain.then(() => this.reqDeleteOverride.request({
                id: override.id,
                orgnr: override.companyid,
            }) as Promise<void>);
        }, Promise.resolve()).then(() => {
            if (overrides.some(o => o.id)) {
                nsUserPermissions.invalidatePermissionCaches();
            }
        });
    }

    /** Undo soft-delete of an override via `restoreoverride` (`*.delete.pending` → active again). */
    restorePermissionOverride(override: IOverride): Promise<void> {
        return requestRestoreOverride(this.reqRestoreOverride, override)
            .then(() => {
                nsUserPermissions.invalidatePermissionCaches();
                permissionSnackbar('restored');
            });
    }

    /**
     * Append one or more new manual permissions via updatepermission (supports new tenants).
     * Caller must refresh — new SP ids are not returned by updatepermission.
     */
    addManualPermissions(
        user: IObjectData,
        panel: OrganizationPanel,
        newPermissions: ISystemPermission[],
        visibleOrganizationIds: ReadonlySet<string>,
    ): Promise<void> {
        if (!newPermissions.length) {
            return rejectInvalidPermission();
        }
        // New tenant may not be on user.permission yet — use shell-aware builder.
        const organizations = buildOrganizationsForUpdate(
            user,
            visibleOrganizationIds,
            panel.organization.organizationid,
            sps => [...sps, ...newPermissions],
            panel.organization,
        );
        return this.reqUpdatePermission
            .request({jobject: JSON.stringify(nsUserPermissions.buildUpdatePermissionJobject(user, organizations))})
            .then(() => {
                nsUserPermissions.invalidatePermissionCaches();
                permissionSnackbar('saved', newPermissions.length);
            });
    }

    abort(): void {
        this.reqUpdatePermission.abort();
        this.reqCreateOverride.abort();
        this.reqDeleteOverride.abort();
        this.reqRestoreOverride.abort();
    }
}

/** Keep getobject-shaped user.permission in sync after a local manual SP mutate. */
function patchOrgSystemPermissions(
    user: IObjectData,
    organizationId: string,
    transform: (sps: ISystemPermission[]) => ISystemPermission[],
): void {
    const org = (user.permission ?? []).find(o => o.organizationid == organizationId);
    if (!org) {
        return;
    }
    org.systempermission = transform(org.systempermission ?? []);
}

/**
 * updatepermission org list from getobject permission arrays, transforming one org's SPs.
 * Prefer user.permission over merged UI rows so override-only synthetics are not written back.
 * `targetOrgShell` is used when the target tenant is not yet on the user (Add eAdm Tenant).
 */
function buildOrganizationsForUpdate(
    user: IObjectData,
    visibleOrganizationIds: ReadonlySet<string>,
    targetOrganizationId: string,
    transformTarget: (sps: ISystemPermission[]) => ISystemPermission[],
    targetOrgShell?: IOrganization,
): IOrganization[] {
    const organizations = (user.permission ?? [])
        .filter(o => visibleOrganizationIds.has(o.organizationid))
        .map(o => {
            const baseline = _.cloneDeep(o);
            if (o.organizationid == targetOrganizationId) {
                baseline.systempermission = transformTarget(baseline.systempermission ?? []);
            }
            return baseline;
        });

    if (!organizations.some(o => o.organizationid == targetOrganizationId)) {
        const shellSource = targetOrgShell
            ?? global.login.getOrganizationById(targetOrganizationId);
        if (!shellSource) {
            return organizations;
        }
        const shell = _.cloneDeep(shellSource);
        shell.systempermission = transformTarget([]);
        organizations.push(shell);
    }

    return organizations;
}
